import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import * as MuiIcons from "@mui/icons-material";
import Close from "@mui/icons-material/Close";
import {
  CircularProgress,
  DialogActions,
  DialogTitle,
  IconButton,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import {
  SubstructureContent,
  SubstructureLabel,
  useConfigurationObjectScrolling,
} from "@plansoft-configuration-renderer/components";
import { ConfigurationObjectResult } from "@plansoft-configuration-renderer/configuration-api";
import { useCsConfigurationApi } from "@plansoft-configuration-renderer/framework";
import { GatewayDest } from "@plansoft-configuration-renderer/gateway";

import { StickyCadContainerProvider } from "@/context/StickyCadContext";

export function SubstructureDialog({
  substructure,
  iconKey,
  awaitFunction,
}: {
  iconKey?: string;
  substructure: ConfigurationObjectResult;
  awaitFunction?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Scroll container the CAD viewer pins to.
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(
    null,
  );
  const { isComplete } = substructure;
  const { invokeUserDefinedFunction, reloadConfiguration } =
    useCsConfigurationApi();

  const elementRef = useConfigurationObjectScrolling<HTMLButtonElement>({
    configurationObject: substructure,
  });

  async function handleOpen(): Promise<void> {
    if (awaitFunction) {
      setIsLoading(true);
      try {
        await invokeUserDefinedFunction({
          name: awaitFunction,
          input: JSON.stringify({ cfgObjectId: substructure.id }),
          path: substructure.path,
        });
      } catch (error: unknown) {
        console.error(`${awaitFunction} user-defined function failed:`, error);
        return;
      } finally {
        setIsLoading(false);
      }
    }
    setIsOpen(true);
  }

  async function handleClose(): Promise<void> {
    setIsOpen(false);
    try {
      await invokeUserDefinedFunction({
        name: "DialogClosed",
        input: JSON.stringify({ cfgObjectId: substructure.id }),
        path: "",
      });
      reloadConfiguration();
    } catch (error: unknown) {
      console.error("DialogClosed user-defined function failed:", error);
    }
  }

  const getIcon = (): React.ReactNode => {
    if (iconKey) {
      const IconComponent = (MuiIcons as Record<string, React.ComponentType>)[
        iconKey
      ];
      if (IconComponent) {
        return <IconComponent />;
      }
      console.warn(`Icon "${iconKey}" not found in MUI icons library`);
    }
    return <></>;
  };

  return (
    <>
      <Button
        ref={elementRef}
        onClick={handleOpen}
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={20} /> : getIcon()}
        type="button"
        variant="outlined"
        sx={{ textTransform: "inherit", width: 250 }}
      >
        <SubstructureLabel substructure={substructure} />
      </Button>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        sx={{ zIndex: 800 }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ minHeight: "3rem" }}>
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "black",
              backgroundColor: "white",
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent ref={setScrollContainer} sx={{ position: "relative" }}>
          {/* Pin the CAD overlay to the dialog so it stays visible on scroll. */}
          <StickyCadContainerProvider container={scrollContainer}>
            <Box display="flex" minHeight="0" flex="1">
              <GatewayDest
                name={"navigation_" + substructure.id}
                style={{ display: "flex", minHeight: 0 }}
                childStyle={{ display: "flex", minHeight: 0 }}
              />
              <Box
                minWidth="500px"
                sx={{ marginLeft: "1rem" }}
                flex="1"
                position="relative"
              >
                <SubstructureContent substructure={substructure} />
              </Box>
            </Box>
          </StickyCadContainerProvider>
        </DialogContent>
        {isComplete && (
          <DialogActions>
            <Box ml="auto">
              <Button type="button" variant="contained" onClick={handleClose}>
                {t("close_configuration_button", "Close")}
              </Button>
            </Box>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
